import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './FactoryGallery.css';

import BcsCuttingArea from '../assets/factoryImages/BcsCuttingArea.png';
import BoppPrintingArea from '../assets/factoryImages/BoppPrintingArea.png';
import ChillerPlantArea from '../assets/factoryImages/ChillerPlantArea.png';
import FactoryEntrance from '../assets/factoryImages/FactoryEntrance.png';
import FactoryGateArea from '../assets/factoryImages/FactoryGateArea.png';
import FactoryGridArea from '../assets/factoryImages/FactoryGridArea.png';
import FirePumpTank from '../assets/factoryImages/FirePumpTank.png';
import LaminationPlantArea from '../assets/factoryImages/LaminationPlantArea.png';
import LoadingUnloadingArea from '../assets/factoryImages/LoadingUnloadingArea.png';
import LoomFloorArea from '../assets/factoryImages/LoomFloorArea.png';
import ManualPrintingArea from '../assets/factoryImages/ManualPrintingArea.png';
import PanelArea from '../assets/factoryImages/PanelArea.png';
import PowerHouse from '../assets/factoryImages/PowerHouse.png';
import RawMaterialFeedArea from '../assets/factoryImages/RawMaterialFeedArea.png';
import RawMaterialStorageArea from '../assets/factoryImages/RawMaterialStorageArea.png';
import StorageArea from '../assets/factoryImages/StorageArea.png';
import TapePlantArea from '../assets/factoryImages/TapePlantArea.png';
import TapePlantFullImage from '../assets/factoryImages/TapePlantFullImage.png';
import TransformerAndMeterRoom from '../assets/factoryImages/TransformerAndMeterRoom.png';

const galleryItems = [
  { src: FactoryEntrance, title: 'Factory Entrance', category: 'Infrastructure' },
  { src: FactoryGateArea, title: 'Factory Gate Area', category: 'Infrastructure' },
  { src: FactoryGridArea, title: 'Factory Grid Area', category: 'Infrastructure' },
  { src: TapePlantFullImage, title: 'Tape Plant — Full View', category: 'Production' },
  { src: TapePlantArea, title: 'Tape Plant Area', category: 'Production' },
  { src: LoomFloorArea, title: 'Loom Floor Area', category: 'Production' },
  { src: LaminationPlantArea, title: 'Lamination Plant Area', category: 'Production' },
  { src: BoppPrintingArea, title: 'BOPP Printing Area', category: 'Printing' },
  { src: ManualPrintingArea, title: 'Manual Printing Area', category: 'Printing' },
  { src: BcsCuttingArea, title: 'BCS Cutting Area', category: 'Production' },
  { src: RawMaterialFeedArea, title: 'Raw Material Feed Area', category: 'Material Handling' },
  { src: RawMaterialStorageArea, title: 'Raw Material Storage', category: 'Material Handling' },
  { src: StorageArea, title: 'Storage Area', category: 'Material Handling' },
  { src: LoadingUnloadingArea, title: 'Loading & Unloading Area', category: 'Logistics' },
  { src: PowerHouse, title: 'Power House', category: 'Utilities' },
  { src: TransformerAndMeterRoom, title: 'Transformer & Meter Room', category: 'Utilities' },
  { src: PanelArea, title: 'Panel Area', category: 'Utilities' },
  { src: ChillerPlantArea, title: 'Chiller Plant Area', category: 'Utilities' },
  { src: FirePumpTank, title: 'Fire Pump Tank', category: 'Safety' }
];

const categories = ['All', ...Array.from(new Set(galleryItems.map((g) => g.category)))];

const FactoryGallery = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeIndex, setActiveIndex] = useState(null);
  const [loadedImages, setLoadedImages] = useState({});

  const filteredItems = useMemo(
    () =>
      activeCategory === 'All'
        ? galleryItems
        : galleryItems.filter((item) => item.category === activeCategory),
    [activeCategory]
  );

  const openLightbox = useCallback((index) => {
    setActiveIndex(index);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeLightbox = useCallback(() => {
    setActiveIndex(null);
    document.body.style.overflow = '';
  }, []);

  const showPrev = useCallback(() => {
    setActiveIndex((prev) =>
      prev === null ? null : (prev - 1 + filteredItems.length) % filteredItems.length
    );
  }, [filteredItems.length]);

  const showNext = useCallback(() => {
    setActiveIndex((prev) =>
      prev === null ? null : (prev + 1) % filteredItems.length
    );
  }, [filteredItems.length]);

  useEffect(() => {
    if (activeIndex === null) return undefined;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') showPrev();
      else if (e.key === 'ArrowRight') showNext();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [activeIndex, closeLightbox, showPrev, showNext]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleImageLoad = (src) => {
    setLoadedImages((prev) => ({ ...prev, [src]: true }));
  };

  const activeItem = activeIndex !== null ? filteredItems[activeIndex] : null;

  return (
    <section className="factory-gallery section">
      <div className="container">
        <div className="section-header text-center">
          <span className="factory-gallery-eyebrow">Our Facility</span>
          <h2 className="section-title">Factory Gallery</h2>
          <p className="section-subtitle">
            A walkthrough of our integrated manufacturing facility — from raw material
            intake to finished goods dispatch.
          </p>
        </div>

        <div className="factory-gallery-filters" role="tablist" aria-label="Gallery categories">
          {categories.map((category) => (
            <button
              key={category}
              type="button"
              role="tab"
              aria-selected={activeCategory === category}
              className={`factory-gallery-filter ${
                activeCategory === category ? 'is-active' : ''
              }`}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        <div className="factory-gallery-grid">
          {filteredItems.map((item, index) => {
            const isLoaded = loadedImages[item.src];
            return (
              <figure
                key={`${item.title}-${index}`}
                className={`factory-gallery-card ${isLoaded ? 'is-loaded' : ''}`}
                tabIndex={0}
                role="button"
                aria-label={`View ${item.title}`}
                onClick={() => openLightbox(index)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openLightbox(index);
                  }
                }}
              >
                <div className="factory-gallery-image-wrap">
                  <img
                    src={item.src}
                    alt={item.title}
                    loading="lazy"
                    onLoad={() => handleImageLoad(item.src)}
                  />
                  <div className="factory-gallery-overlay" aria-hidden="true">
                    <div className="factory-gallery-overlay-inner">
                      <span className="factory-gallery-category">{item.category}</span>
                      <h3 className="factory-gallery-title">{item.title}</h3>
                      <span className="factory-gallery-expand">
                        <i className="fas fa-expand"></i> View
                      </span>
                    </div>
                  </div>
                </div>
                <figcaption className="factory-gallery-caption">{item.title}</figcaption>
              </figure>
            );
          })}
        </div>
      </div>

      {activeItem && (
        <div
          className="factory-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={`${activeItem.title} — enlarged view`}
          onClick={closeLightbox}
        >
          <button
            type="button"
            className="factory-lightbox-close"
            onClick={closeLightbox}
            aria-label="Close"
          >
            <i className="fas fa-times"></i>
          </button>
          <button
            type="button"
            className="factory-lightbox-nav factory-lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              showPrev();
            }}
            aria-label="Previous image"
          >
            <i className="fas fa-chevron-left"></i>
          </button>
          <div
            className="factory-lightbox-content"
            onClick={(e) => e.stopPropagation()}
          >
            <img src={activeItem.src} alt={activeItem.title} />
            <div className="factory-lightbox-caption">
              <span className="factory-lightbox-category">{activeItem.category}</span>
              <h3>{activeItem.title}</h3>
              <span className="factory-lightbox-counter">
                {activeIndex + 1} / {filteredItems.length}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="factory-lightbox-nav factory-lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              showNext();
            }}
            aria-label="Next image"
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      )}
    </section>
  );
};

export default FactoryGallery;
